import qs from 'qs'
import http from 'src/infrastructure/http'
import httpRemotePrinterRaspberrySoul from 'src/infrastructure/httpRemotePrinterRaspberrySoul'
import { parseFailedResponse, parseGoodResponse } from 'src/infrastructure/parse-axios-response'
import { formatDateIsoReplaceHour } from 'src/utils/formatDate'
import { mapToUpperCase } from 'src/utils/mapToUpperCase'
import {
  AvailableRestockLists,
  FiltersSearchLists,
  GenerateListsPayload,
  GenericApiErrors,
  GetAvailableRestockListsPayload,
  GetProductsListByCSVApiParams,
  FinishRestockingAddressPayload,
  ItemsListPayload,
  ProductsList,
  ProductsListPayload,
  RemoveRestockingItemPayload,
  RestockingItemsList,
  RestockingListPages,
  RestockingLists,
  RestockingListTypesEnum,
  SearchRestockListsPayload,
  StartRestockingAddressPayload,
  StartRestockingListPagePayload,
  UpdateRestockingListPayload,
  ValidatedBadge,
  WarningDeleteRestockingList,
  ItemToRestock,
  ValidateBadgePayload,
  RestockingFullcartPayload,
  OperatorUnlinkPayload,
  ResponseStartedPage,
  StartOrFinishRestockingAddressRestockPayload,
  FinishItemRestockUnfit,
  OperationData,
  ValidateProductPayload,
  ValidateRestockAddressPayload,
  ReportItemQuantityPerBoxPayload,
  RestockingPageDetails,
  RestockingListType,
  CheckInValidatePayload,
  CheckinGenerateRestockListPayload,
  CheckinGenerateRestockListResponse,
  ValidateCollectAddressPayload,
  FinishCollectAddressEmptyGondolaPayload,
  RestockingValidateEntryCollectAddressPayload,
  MovingAddress,
} from './RestockingTypes'
import { transferTypes } from './utils'

const getProductsList = async (
  payload: ProductsListPayload,
) => {
  try {
    const {
      fulfillmentCenterId, shedCode, sourceType,
    } = payload

    const body = {
      id_fulfillment_center: fulfillmentCenterId,
      shed_codes: shedCode,
      restocking_type: sourceType,
    }

    const result = await http.post('/v2/restocking/products', body)

    return parseGoodResponse<ProductsList>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getProductsListByCSV = async (payload: GetProductsListByCSVApiParams) => {
  const { csvFile, idType } = payload

  const formData = new FormData()

  formData.append('csv_file', csvFile)
  formData.append('id_type', idType.toString())

  try {
    const result = await http.post(
      '/v2/restocking/by-csv',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )

    return parseGoodResponse<ProductsList>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const generateRestockingLists = async (
  payload: GenerateListsPayload,
) => {
  try {
    const {
      fulfillmentCenterId, quantityPerPage, quantityToRestocking, products,
      generatedCode, sourceType, totalSkus, idType, level, priority, shippingDate, idFulfillmentCenterDestination,
    } = payload
    let productsListFormatted: any
    let body: any

    if (idType === RestockingListTypesEnum.RESTOCKING_FRESH_AUTOMATIC) {
      productsListFormatted = products.map((product) => ({
        product_code: product.productCode,
        product_description: product.productDescription,
        restock_quantity: product.restockQuantity,
        source_addresses: product.sourceAddresses.map((sourceAddress) => ({
          address: sourceAddress.address,
          quantity: sourceAddress.quantity,
          expiration_date: formatDateIsoReplaceHour(new Date(sourceAddress.expirationDate)),
        })),
        minimum_expiration_date: formatDateIsoReplaceHour(new Date(product.minimumExpirationDate)),
        destination_address: product.destinationAddress,
        id_model: product.idModel,
        no_reserve: product.noReserve,
        shed_code: product.shedCode,
        street: product.street,
        no_capacity: product.noCapacity,
      }))

      body = {
        id_fulfillment_center: fulfillmentCenterId,
        skus_per_list: quantityPerPage,
        skus_to_restock: quantityToRestocking,
        products_list: productsListFormatted,
        generated_code: generatedCode,
        source_type: sourceType,
        total_skus: totalSkus,
        id_type: idType,
      }
    } else {
      productsListFormatted = products.map((product) => ({
        product_code: product.productCode,
        product_description: product.productDescription,
        restock_quantity: product.restockQuantity,
        source_addresses: product.sourceAddresses.map((sourceAddress) => ({
          address: sourceAddress.address,
          quantity: sourceAddress.quantity,
          expiration_date: formatDateIsoReplaceHour(new Date(sourceAddress.expirationDate)),
        })),
        minimum_expiration_date: formatDateIsoReplaceHour(new Date(product.minimumExpirationDate)),
        destination_address: product.destinationAddress,
        id_model: product.idModel,
        street: product.street,
        type: product.type,
        page_number: product.pageNumber,
        pre_defined_qty_divergence: product.preDefinedQtyDivergence,
      }))

      body = {
        products_list: productsListFormatted,
        id_fulfillment_center: fulfillmentCenterId,
        id_type: idType,
        level,
        priority,
        shipping_date: transferTypes.includes(idType) ? shippingDate : null,
        id_fulfillment_center_destination: transferTypes.includes(idType) ? idFulfillmentCenterDestination : null,
      }
    }

    await http.post('/v2/restocking/generate-lists', body)

    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getRestockingListPages = async (
  idList: number,
) => {
  try {
    const result = await http.get(`/v2/restocking/list/pages/${idList}`)
    return parseGoodResponse<RestockingListPages[]>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const searchRestockingLists = async (
  payload: SearchRestockListsPayload,
) => {
  try {
    const {
      idFulfillmentCenter,
      createdDate,
      priority,
      sourceType,
      level,
      idType,
      idStatus,
    } = payload

    const body = {
      id_fulfillment_center: idFulfillmentCenter,
      created_date: formatDateIsoReplaceHour(createdDate),
      priority: mapToUpperCase(priority),
      source_type: mapToUpperCase(sourceType),
      level: mapToUpperCase(level),
      id_type: idType,
      id_status: idStatus,
    }

    const result = await http.post('/v2/restocking/lists', body)

    return parseGoodResponse<RestockingLists[]>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const removeRestockingItem = async (payload: RemoveRestockingItemPayload) => {
  try {
    const { commonId: idList, endpoint } = payload
    const result = await http.delete(`/v2/restocking/delete-list/${idList}${endpoint !== undefined ? endpoint : ''}`)
    return parseGoodResponse<WarningDeleteRestockingList>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getItemsList = async (
  payload: ItemsListPayload,
) => {
  try {
    const result = await http.post('/v2/restocking/list/pages/items', payload)
    return parseGoodResponse<RestockingItemsList[]>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

export const validateBadge = async (payload: ValidateBadgePayload) => {
  try {
    const { badge, createList = false } = payload
    const resultSuccess = await http.post('/v2/restocking/validate-badge', { create_list: createList }, {
      headers: { 'user-barcode': badge, 'create-list': true },
    })
    return parseGoodResponse<ValidatedBadge>(resultSuccess.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const startRestockingList = async (
  payload: UpdateRestockingListPayload,
) => {
  try {
    const { badge, idList } = payload
    await http.put(`/v2/restocking/start-list/${idList}`, null, {
      headers: { 'user-barcode': badge },
    })
    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const finishRestockingList = async (
  payload: UpdateRestockingListPayload,
) => {
  try {
    const { badge, idList } = payload
    await http.put(`/v2/restocking/finish-list/${idList}`, null, {
      headers: { 'user-barcode': badge },
    })
    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getFiltersSearchLists = async (isForApp: boolean) => {
  try {
    const result = await http.get(`/v2/restocking/search-filters?is_for_app=${isForApp}`)
    return parseGoodResponse<FiltersSearchLists>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getAvailableRestockLists = async (
  payload: GetAvailableRestockListsPayload,
) => {
  const {
    idFulfillmentCenter,
    hasForkliftAuth,
    idType,
    badge,
  } = payload

  const body = {
    id_fulfillment_center: idFulfillmentCenter,
    has_forklift_auth: hasForkliftAuth,
    id_type: idType,
  }

  try {
    const result = await http.post('/v2/restocking/available-lists', body, {
      headers: { 'user-barcode': badge },
    })
    return parseGoodResponse<AvailableRestockLists[]>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const startRestockingListPage = async (
  payload: StartRestockingListPagePayload,
) => {
  try {
    const {
      idList, badge, acceptPaused = null,
    } = payload

    const body = { accept_paused: acceptPaused }

    const result = await http.put(`/v2/restocking/start-page/${idList}`, body, {
      headers: { 'user-barcode': badge },
    })

    return parseGoodResponse<ResponseStartedPage>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const startRestockingAddress = async (
  payload: StartRestockingAddressPayload,
) => {
  try {
    const {
      itemsAddresses, idPage, badge, idFulfillmentCenter,
    } = payload

    const itemsAddressesFormatted = itemsAddresses.map((itemAddress) => ({
      id_item: itemAddress.idItem,
      ids_item_address: itemAddress.idsItemAddress,
    }))

    const body = {
      items_addresses: itemsAddressesFormatted,
      id_page: idPage,
      id_fulfillment_center: idFulfillmentCenter,
    }

    await http.put('/v2/restocking/start-address/collect', body, {
      headers: { 'user-barcode': badge },
    })
    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const finishRestockingAddress = async (payload: FinishRestockingAddressPayload) => {
  try {
    const {
      itemAddress, idPage, badge, idFulfillmentCenter,
    } = payload

    const itemAddressFormatted = {
      id_item: itemAddress.idItem,
      ids_item_address: itemAddress.idsItemAddress,
      quantity: itemAddress.quantity,
      quantity_to_collect: itemAddress.quantityToCollect,
    }

    const body = {
      item_address: itemAddressFormatted,
      id_page: idPage,
      id_fulfillment_center: idFulfillmentCenter,
    }

    const result = await http.put('/v2/restocking/finish-address/collect', body, {
      headers: { 'user-barcode': badge },
    })
    return parseGoodResponse<OperationData>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const restockingFullcart = async (payload: RestockingFullcartPayload) => {
  try {
    const {
      badge, idPage, idsItemAddress,
    } = payload

    const body = {
      id_page: idPage,
      ids_item_address: idsItemAddress,
    }

    const result = await http.put('/v2/restocking/fullcart', body, {
      headers: { 'user-barcode': badge },
    })
    return parseGoodResponse<ItemToRestock[]>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const operatorUnlink = async (payload: OperatorUnlinkPayload) => {
  try {
    const { badge, idPage, origin } = payload

    await http.patch(`/v2/restocking/operator/unlink/${idPage}`, {}, {
      headers: { 'user-barcode': badge },
      params: { origin },
    })
    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const startRestockingAddressRestock = async (
  payload: StartOrFinishRestockingAddressRestockPayload,
) => {
  try {
    const { idItem, badge } = payload
    await http.put(`/v2/restocking/start-address/restock/${idItem}`, {}, {
      headers: { 'user-barcode': badge },
    })
    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const finishRestockingAddressRestock = async (
  payload: StartOrFinishRestockingAddressRestockPayload,
) => {
  try {
    const { idItem, itemsToFinish, badge } = payload
    const url = idItem ? `/v2/restocking/finish-address/restock/${idItem}` : '/v2/restocking/finish-address/restock/all'

    const idsItems = itemsToFinish?.map((item) => item.idPageItem)

    const body = idItem ? {} : { ids_items: idsItems }
    const result = await http.put(url, body, { headers: { 'user-barcode': badge } })
    return parseGoodResponse<OperationData>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const finishRestockingAddressRestockUnfit = async (payload: FinishItemRestockUnfit) => {
  try {
    const {
      idFulfillmentCenter,
      idPage,
      idItem,
      quantity,
      badge,
    } = payload

    const body = {
      id_fulfillment_center: idFulfillmentCenter,
      id_page: idPage,
      id_item: idItem,
      quantity,
    }

    const result = await http.put('/v2/restocking/finish-address/restock/unfit', body, {
      headers: { 'user-barcode': badge },
    })
    return parseGoodResponse<ItemToRestock[]>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const restockingValidateProduct = async (
  payload: ValidateProductPayload,
) => {
  try {
    const {
      badge, idModel, productBarcode, idPage,
    } = payload

    const body = {
      id_model: idModel,
      product_barcode: productBarcode,
      id_page: idPage,
    }

    await http.post('/v2/restocking/validate-product', body, {
      headers: { 'user-barcode': badge },
    })
    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const restockingValidateRestockAddress = async (
  payload: ValidateRestockAddressPayload,
) => {
  try {
    const {
      badge,
      idPageItem,
      address,
      isGondola,
      idFulfillmentCenter,
    } = payload

    const body = {
      id_page_item: idPageItem,
      address,
      is_gondola: isGondola,
      id_fulfillment_center: idFulfillmentCenter,
    }

    await http.post('/v2/restocking/validate-restock-address', body, {
      headers: { 'user-barcode': badge },
    })
    return parseGoodResponse<never>()
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const restockingReportQuantityPerBox = async (
  payload: ReportItemQuantityPerBoxPayload,
) => {
  try {
    const { badge, idItem, newQuantityPerBox } = payload

    const body = {
      id_item: idItem,
      new_quantity_per_box: newQuantityPerBox,
    }

    const result = await http.post('/v2/restocking/report/quantity-per-box', body, {
      headers: { 'user-barcode': badge },
    })
    return parseGoodResponse<OperationData>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getRestockingPageDetails = async (
  idPage: Number,
) => {
  try {
    const result = await http.get(`/v2/restocking/page/${idPage}/details`)
    return parseGoodResponse<RestockingPageDetails>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getRestockingTypes = async () => {
  try {
    const result = await http.get('/v2/restocking/restocking-types')
    return parseGoodResponse<RestockingListType[]>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const restockingCheckinValidate = async (
  payload: CheckInValidatePayload,
) => {
  try {
    const { badge, orderBoxCode, idRestockingType } = payload

    const body = {
      order_box_code: orderBoxCode,
      id_restocking_type: idRestockingType,
    }

    const result = await http.post('/v2/restocking/check-in/validate', body, {
      headers: { 'user-barcode': badge },
    })
    return parseGoodResponse<string>(result.data.data.order_code)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const generateCheckinRestockList = async (
  payload: CheckinGenerateRestockListPayload,
) => {
  try {
    const {
      badge, idFulfillmentCenter, idType, ordersCode,
    } = payload

    const body = {
      id_fulfillment_center: idFulfillmentCenter,
      id_type: idType,
      orders_code: ordersCode,
    }

    const result = await http.post('/v2/restocking/check-in/generate-list', body, {
      headers: { 'user-barcode': badge },
    })
    return parseGoodResponse<CheckinGenerateRestockListResponse>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const validateCollectAddress = async (payload: ValidateCollectAddressPayload) => {
  try {
    const {
      badge, idsModels, address, addressToValidate, isGondola,
    } = payload

    const body = {
      ids_models: idsModels,
      address,
      address_to_validate: addressToValidate,
      is_gondola: isGondola,
    }

    const result = await http.post('/v2/restocking/validate-collect-address', body, {
      headers: { 'user-barcode': badge },
    })

    return parseGoodResponse<RestockingListType[]>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const finishCollectAddressEmptyGondola = async (payload: FinishCollectAddressEmptyGondolaPayload) => {
  try {
    const {
      idFulfillmentCenter, idPage, idItem, idItemAddress, quantityToCollect, batches, badge,
    } = payload

    const body = {
      id_fulfillment_center: idFulfillmentCenter,
      id_page: idPage,
      id_item: idItem,
      id_item_address: idItemAddress,
      quantity_to_collect: quantityToCollect,
      batches: batches.map((batch) => ({
        quantity: batch.quantity,
        expiration_date: formatDateIsoReplaceHour(batch.expirationDate),
      })),
    }

    const result = await http.put('/v2/restocking/finish-address/collect/empty-gondola', body, {
      headers: { 'user-barcode': badge },
    })

    return parseGoodResponse<OperationData>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const restockingValidateEntryCollectAddress = async (payload: RestockingValidateEntryCollectAddressPayload) => {
  try {
    const { badge, idPage, address } = payload

    const body = {
      id_page: idPage,
      address,
    }

    const result = await http.post('/v2/restocking/validate-entry-collect-address', body, {
      headers: { 'user-barcode': badge },
    })

    return parseGoodResponse<OperationData>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

const getMovingAddress = async (
  address_type: number[],
  fc: number[],
  availability: string,
) => {
  try {
    const result = await http.get('/v2/restocking/get-moving-address', {
      params: { address_type, fc, availability },
      paramsSerializer: {
        serialize: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
      },
    })
    return parseGoodResponse<MovingAddress[]>(result.data.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>('Failed', err.response)
  }
}

const printAddressesLabel = async (payload: string[]) => {
  try {
    const result = await httpRemotePrinterRaspberrySoul.post('/addresses/print', {
      addresses: payload,
    })
    return parseGoodResponse<any>(result.data)
  } catch (err: any) {
    return parseFailedResponse<any, any>(GenericApiErrors.Failed, err.response)
  }
}

export default {
  getProductsList,
  getProductsListByCSV,
  generateRestockingLists,
  getRestockingListPages,
  removeRestockingItem,
  getItemsList,
  validateBadge,
  startRestockingList,
  finishRestockingList,
  searchRestockingLists,
  getFiltersSearchLists,
  getAvailableRestockLists,
  startRestockingListPage,
  startRestockingAddress,
  finishRestockingAddress,
  restockingFullcart,
  operatorUnlink,
  startRestockingAddressRestock,
  finishRestockingAddressRestock,
  finishRestockingAddressRestockUnfit,
  restockingValidateProduct,
  restockingValidateRestockAddress,
  restockingReportQuantityPerBox,
  getRestockingPageDetails,
  getRestockingTypes,
  restockingCheckinValidate,
  generateCheckinRestockList,
  validateCollectAddress,
  finishCollectAddressEmptyGondola,
  restockingValidateEntryCollectAddress,
  getMovingAddress,
  printAddressesLabel,
}
